import { useEffect, useRef } from "react"

interface WeatherParticlesProps {
  weatherId: number
  isDay: boolean
  className?: string
}

interface Particle {
  x: number
  y: number
  speed: number
  size: number
  opacity: number
  wind: number
}

function getParticleConfig(weatherId: number) {
  if (weatherId >= 200 && weatherId < 300) {
    return { type: "rain", density: 120, color: "160 180 255", heavy: true }
  }
  if (weatherId >= 300 && weatherId < 400) {
    return { type: "drizzle", density: 80, color: "160 180 255", heavy: false }
  }
  if (weatherId >= 500 && weatherId < 600) {
    return { type: "rain", density: 150, color: "160 180 255", heavy: true }
  }
  if (weatherId >= 600 && weatherId < 700) {
    return { type: "snow", density: 60, color: "220 230 255", heavy: false }
  }
  if (weatherId >= 700 && weatherId < 800) {
    return { type: "fog", density: 30, color: "180 190 210", heavy: false }
  }
  if (weatherId === 800) {
    return { type: "clear", density: 20, color: "255 255 255", heavy: false }
  }
  return { type: "clouds", density: 25, color: "200 210 230", heavy: false }
}

export function WeatherParticles({ weatherId, isDay, className }: WeatherParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particlesRef = useRef<Particle[]>([])
  const animFrameRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const config = getParticleConfig(weatherId)

    const resize = () => {
      const parent = canvas.parentElement
      if (!parent) return
      canvas.width = parent.clientWidth
      canvas.height = parent.clientHeight
    }
    resize()
    window.addEventListener("resize", resize)

    const initParticles = () => {
      particlesRef.current = Array.from({ length: config.density }, () => ({
        x: Math.random() * canvas.width * 1.2 - canvas.width * 0.1,
        y: Math.random() * canvas.height * 1.2 - canvas.height * 0.1,
        speed: config.heavy ? 3 + Math.random() * 8 : 0.8 + Math.random() * 3,
        size: config.heavy ? 1 + Math.random() * 1.5 : 0.5 + Math.random() * 1,
        opacity: 0.15 + Math.random() * 0.3,
        wind: -0.5 + Math.random() * 1,
      }))
    }
    initParticles()

    let frame = 0

    const draw = () => {
      frame++
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (const p of particlesRef.current) {
        ctx.beginPath()

        if (config.type === "snow") {
          p.x += Math.sin(frame * 0.02 + p.y * 0.01) * 0.3 + p.wind * 0.3
          p.y += p.speed * 0.4
          p.size = 1 + Math.random() * 2
        } else if (config.type === "rain" || config.type === "drizzle") {
          p.x += p.wind * 0.8
          p.y += p.speed
          ctx.moveTo(p.x, p.y)
          ctx.lineTo(p.x - p.wind * 2, p.y - 4 - p.speed * 1.5)
        } else if (config.type === "fog") {
          p.x += Math.sin(frame * 0.005 + p.y * 0.003) * 0.15
          p.y += p.speed * 0.1
          p.size = 8 + Math.random() * 15
        } else if (config.type === "clouds") {
          p.x += p.speed * 0.15
          p.y += Math.sin(frame * 0.01 + p.x * 0.005) * 0.1
          p.size = 6 + Math.random() * 12
        } else {
          p.x += Math.sin(frame * 0.01 + p.y * 0.005) * 0.1
          p.y += p.speed * 0.08
          p.size = 0.5 + Math.random() * 1
        }

        if (p.y > canvas.height + 10) { p.y = -10; p.x = Math.random() * canvas.width }
        if (p.y < -10) { p.y = canvas.height + 10 }
        if (p.x > canvas.width + 10) p.x = -10
        if (p.x < -10) p.x = canvas.width + 10

        ctx.strokeStyle = `rgb(${config.color} / ${p.opacity})`
        ctx.fillStyle = `rgb(${config.color} / ${p.opacity * 0.7})`

        if (config.type === "snow") {
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.fill()
        } else if (config.type === "rain" || config.type === "drizzle") {
          ctx.lineWidth = p.size * 0.5
        } else {
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
          ctx.fill()
        }

        ctx.stroke()
      }

      animFrameRef.current = requestAnimationFrame(draw)
    }
    draw()

    return () => {
      cancelAnimationFrame(animFrameRef.current)
      window.removeEventListener("resize", resize)
    }
  }, [weatherId, isDay])

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
    />
  )
}
